import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useUser } from '../../context/UserContext';
import { useTenant } from '../../context/TenantContext';
import { useMembership } from '../../context/MembershipContext';
import {
  publishOfficialAnswer,
  updateOfficialAnswer,
} from '../../services/questionsService';
import { canAnswerQuestion } from '../../platform/qnaPermissions';
import {
  isRichTextEmpty,
  RICH_TEXT_EDITOR_FORMATS,
  RICH_TEXT_EDITOR_MODULES,
  toDisplayHtml,
  useRichTextToolbarTitles,
} from '../../platform/richTextEditor';
import '../../platform/platform-rich-text-editor.css';
import './QnAAnswerForm.css';
import { useToast } from '../../platform/toast/ToastProvider';

function QnAAnswerForm({
  questionId,
  question,
  isConversationReply = false,
  editingAnswer = null,
  onPublished,
  onCancel,
  embedded = false,
}) {
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { user } = useUser();
  const { tenantId } = useTenant();
  const { memberships } = useMembership();
  const toast = useToast();
  const modules = useMemo(() => RICH_TEXT_EDITOR_MODULES, []);
  const quillContainerRef = useRichTextToolbarTitles();

  const canAnswer = canAnswerQuestion(memberships, tenantId, user, question);
  const isEditing = Boolean(editingAnswer);

  useEffect(() => {
    setText(toDisplayHtml(editingAnswer?.text || ''));
  }, [editingAnswer?.id, editingAnswer?.text]);

  if (!canAnswer) {
    return null;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isRichTextEmpty(text) || !user || !tenantId) {
      setError('Odgovor ne sme biti prazen.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      if (isEditing) {
        await updateOfficialAnswer({
          questionId,
          answerId: editingAnswer.id,
          text,
          user,
        });
        toast.success('Odgovor je bil posodobljen.');
      } else {
        await publishOfficialAnswer({
          activeTenantId: tenantId,
          questionId,
          text,
          user,
        });
        setText('');
        toast.success('Odgovor je bil objavljen.');
      }

      if (onPublished) {
        onPublished();
      }
    } catch (err) {
      console.error('[qna] Failed to publish official answer', err);
      const message = isEditing
        ? 'Shranjevanje popravka ni uspelo. Poskusite znova.'
        : 'Objava odgovora ni uspela. Poskusite znova.';
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const title = isEditing
    ? 'Popravi odgovor'
    : isConversationReply
      ? 'Nov odgovor sindikata'
      : 'Odgovor sindikata';

  const content = (
    <>
      {isEditing && (
        <p className="qna-answer-form__hint">
          Popravite le tipkarske napake. Za nov odgovor uporabite obrazec spodaj.
        </p>
      )}
      {error && <Alert variant="danger">{error}</Alert>}
      <form onSubmit={handleSubmit}>
        <div ref={quillContainerRef} className="platform-rich-text-quill qna-answer-form__editor">
          <ReactQuill
            theme="snow"
            value={text}
            onChange={setText}
            modules={modules}
            formats={RICH_TEXT_EDITOR_FORMATS}
            placeholder="Napišite uradni odgovor…"
          />
        </div>
        <div className="qna-answer-form__actions">
          <Button variant="primary" type="submit" disabled={submitting}>
            {isEditing ? 'Shrani popravek' : isConversationReply ? 'Objavi nov odgovor' : 'Objavi odgovor'}
          </Button>
          {isEditing && onCancel && (
            <Button variant="outline-secondary" type="button" onClick={onCancel} disabled={submitting}>
              Prekliči
            </Button>
          )}
        </div>
      </form>
    </>
  );

  if (embedded) {
    return (
      <div className="qna-answer-form qna-answer-form--embedded">
        <h3 className="qna-embedded-section__title">{title}</h3>
        <div className="qna-answer-form__body">{content}</div>
      </div>
    );
  }

  return (
    <div className={`qna-answer-form platform-content-detail-card${isEditing ? ' qna-answer-form--editing' : ''}`}>
      <div className="platform-content-detail-card__header">
        <h2 className="platform-content-detail-card__title">{title}</h2>
      </div>
      <div className="qna-answer-form__body">{content}</div>
    </div>
  );
}

export default QnAAnswerForm;
